import React, { useContext, useEffect, useMemo } from "react";

import { getFocusableItems, useDeviceInfo, useLocation, XLARGE } from "@swa-ui/browser";
import { Message, PageGrid, Toast, useCaption } from "@swa-ui/core";
import i18n from "@swa-ui/locale";
import { getUniqueId } from "@swa-ui/string";

import { ApplicationContext } from "../ApplicationContextProvider";
import { MessageContext } from "../MessageProvider";
import styles from "./PageMessage.module.scss";

/**
 * PageMessage uses MessageContext to display a Toast, or Message notification
 * under the heading and system messages.
 */

export const PageMessage = () => {
  const { site } = useContext(ApplicationContext);
  const { message } = useContext(MessageContext);
  const { captionRef, hideCaption, isCaptionVisible, showCaption } = useCaption(
    "center",
    "hidden",
    handleToastDismiss
  );
  const { screenSize } = useDeviceInfo();
  const { pathname } = useLocation();
  const messageId = useMemo(() => getUniqueId("pageMessage"), []);

  useEffect(() => {
    if (isCaptionVisible && message?.shouldCloseModalOnRouteChange) {
      handleToastClose();
    }
  }, [pathname]);

  useEffect(() => {
    if (Object.keys(message).length > 0) {
      showCaption();
    } else {
      handleToastClose();
    }
  }, [message]);

  return isCaptionVisible && message?.isPageLevel ? (
    <PageGrid className={styles.layout} maxWidth={!(site === "mobile" && screenSize === XLARGE)}>
      <Message {...getMessageProps()} />
    </PageGrid>
  ) : (
    <Toast {...getToastProps()} />
  );

  function getMessageProps() {
    return {
      "aria-label-close": i18n("PageMessage__ARIA_LABEL_CLOSE_BUTTON"),
      ariaDescribeCloseButton: true,
      children: message.body,
      className: styles.message,
      headingProps: {
        children: message.heading,
        styleLevel: 4,
      },
      id: messageId,
      on: isCaptionVisible,
      onClose: handleToastClose,
      onTransitionEnd: handleTransitionEnd,
      styleType: message.type,
    };
  }

  function getToastProps() {
    return {
      "aria-label-close": i18n("PageMessage__ARIA_LABEL_CLOSE_BUTTON"),
      captionRef,
      children: message.body,
      location: message.location,
      messageProps: {
        headingProps: {
          children: message.heading,
          styleLevel: 4,
        },
        styleType: message.type,
      },
      on: isCaptionVisible,
      onClose: handleToastClose,
      showBackgroundVeil: message.showBackgroundVeil,
    };
  }

  function handleToastClose() {
    hideCaption();
    handleToastDismiss();
  }

  function handleToastDismiss() {
    message?.onMessageClose?.();
  }

  function handleTransitionEnd() {
    focusFirstItem();
  }

  function focusFirstItem() {
    const focusableItems = getFocusableItems(`[id="${messageId}"]`);

    isCaptionVisible && focusableItems?.[0]?.focus();
  }
};

PageMessage.displayName = "PageMessage";
